import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../use-typed-selector';
import { ApiResponse, CrmFieldResponse, User } from 'common/interfaces';
import { useEffect, useMemo, useState } from 'react';
import { useFetch } from 'hooks/use-fetch/use-fetch';
import { USER_ROLES } from '@/constants';
import { useIsUserHaveRole } from '../use-is-user-have-role';
import { PAGES } from 'common/enums';
import { setDownloadManagerInfo, setIndicator } from 'actions/app_action';
import { useQuery } from '@tanstack/react-query';
import { GetIndicatorsData, IndicatorTypes } from 'request-config/indicators/indicators.types';
import { indicatorsRequestConfig } from 'request-config/indicators/indicators.request-config';
import { objectUtility } from 'utils/object/object';
import { IndicatorsResponse } from './use-refresh-indicators.types';

type Indicator<T = unknown> = { [key in IndicatorTypes]: T };

const REFETCH_INTERVAL = 120_000;
const DELAY_OF_FIRST_REQUEST = 3_000;
export const INDICATORS_QUERY_KEY = 'useRefreshIndicators';

export const useRefreshIndicators = () => {
  const dispatch = useDispatch();
  const user = useTypedSelector<User>(state => state['app'].user);
  const isExportsFlowExist = useTypedSelector<boolean>(state => state['app'].isExportsFlowExist);
  const { newFiles, totalFiles, filesInProgress } = useTypedSelector(state => state['app'].downloadManager);
  const isAdminOrFleetManager = useIsUserHaveRole([USER_ROLES.administrator.id, USER_ROLES.fleetManager.id]);
  const [enabled, setEnabled] = useState(false);

  const indicatorsDataToSend: GetIndicatorsData = useMemo(() => {
    const indicatorsToRetrieve: Indicator<boolean> = {
      [IndicatorTypes.Freigabe]:
        isAdminOrFleetManager && user.appPages.some(appPage => appPage.url === PAGES.EINGANGSRECHNUNG),
      [IndicatorTypes.Doctype]:
        isAdminOrFleetManager && user.appPages.some(appPage => appPage.url === PAGES.DOCUMENTMANAGER),
      [IndicatorTypes.Fines]: isAdminOrFleetManager && user.appPages.some(appPage => appPage.url === PAGES.FINES),
      [IndicatorTypes.VehicleRequests]: user.appPages.some(appPage => appPage.url === PAGES.REQUESTS),
      [IndicatorTypes.Orders]: user.appPages.some(appPage => appPage.url === PAGES.ORDERS),
      [IndicatorTypes.LeasingContracts]: user.appPages.some(appPage => appPage.url === PAGES.LEASING_CONTRACTS),
      [IndicatorTypes.CashAdvance]:
        isAdminOrFleetManager && user.appPages.some(appPage => appPage.url === PAGES.CASH_ADVANCE),
      [IndicatorTypes.Exports]: isExportsFlowExist,
      [IndicatorTypes.YourQuestion]: user.appPages.some(appPage => appPage.url === PAGES.SERVICE_PROVIDER_TASKS)
    };

    return {
      types: objectUtility
        .entries(indicatorsToRetrieve)
        .filter(([key, value]) => value)
        .map(([key, value]) => Number(key))
    };
  }, [isAdminOrFleetManager, user.appPages, isExportsFlowExist]);

  const { fetch: getIndicators } = useFetch<ApiResponse<IndicatorsResponse>>({
    isShowLoading: false,
    isShowErrorSnackbar: false,
    ...indicatorsRequestConfig.getIndicators(indicatorsDataToSend)
  });

  const { data: indicatorsData } = useQuery<ApiResponse<IndicatorsResponse>, unknown, IndicatorsResponse>({
    queryKey: [INDICATORS_QUERY_KEY],
    queryFn: getIndicators,
    select: response => response?.data?.data,
    refetchInterval: REFETCH_INTERVAL,
    enabled: enabled && !!indicatorsDataToSend.types.length,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setEnabled(true);
    }, DELAY_OF_FIRST_REQUEST);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (indicatorsData) {
      const indicators: Indicator<CrmFieldResponse<number>> = {
        [IndicatorTypes.Freigabe]: indicatorsData.freigabe?.count ?? null,
        [IndicatorTypes.Doctype]: indicatorsData.ocrEingang?.count ?? null,
        [IndicatorTypes.Fines]: indicatorsData.owi,
        [IndicatorTypes.VehicleRequests]: indicatorsData.vehicles,
        [IndicatorTypes.Orders]: indicatorsData.orders,
        [IndicatorTypes.LeasingContracts]: indicatorsData.leasingContracts,
        [IndicatorTypes.CashAdvance]: indicatorsData.barauslage,
        [IndicatorTypes.Exports]: indicatorsData.downloadManager?.new ?? null,
        [IndicatorTypes.YourQuestion]: indicatorsData.tasks
      };

      dispatch(setIndicator(indicators));
      if (indicatorsData.downloadManager && isExportsFlowExist) {
        const shouldUpdateState =
          newFiles !== indicatorsData.downloadManager.new ||
          totalFiles !== indicatorsData.downloadManager.total ||
          filesInProgress !== indicatorsData.downloadManager.inProgress;

        if (shouldUpdateState) {
          dispatch(
            setDownloadManagerInfo({
              newFiles: indicatorsData.downloadManager.new,
              totalFiles: indicatorsData.downloadManager.total,
              filesInProgress: indicatorsData.downloadManager.inProgress
            })
          );
        }
      }
    }
  }, [indicatorsData, isExportsFlowExist]);
};
